import FormCompanyImage from 'components/form/FormCompanyImage';
import FormText from 'components/form/FormText';
import IndicatorScreen from 'components/indicator/IndicatorScreen';

import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useState, type JSX } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import type { Path, UseFieldArrayReturn } from 'react-hook-form';
import { get, useFormContext } from 'react-hook-form';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import type { DiagnosisEditFormStateType } from 'types/page/diagnosisEditFormStateType';
import WsDiagnosisEditQuestionOptions from 'pages/ws/diagnosisEdit/components/diagnosisEditQuestions/WsDiagnosisEditQuestionOptions';
import { isEmpty, submit } from 'utils/app';
import { convResultListTitleToId } from 'utils/diagnosis';

/**
 * クイズ作成編集 設問設定コンポーネント
 */
export default function WsDiagnosisEditQuestions(props: {
  readOnly: boolean;
  fieldArray: UseFieldArrayReturn<DiagnosisEditFormStateType, 'questions', 'id'>;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();

  const [acctState, setAcctState] = useAtom(AcctState);

  const [processing, setProcessing] = useState(false);
  const { postDiagnosisAiOptions } = useApiState();
  const { noticeMessageSuccess, getActivePlan } = useAppState();

  const {
    control,
    register,
    getValues,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<DiagnosisEditFormStateType>();

  const { append, move, remove, fields } = props.fieldArray;

  const activePlan = getActivePlan();

  // 登録可能最大件数
  const limit = 25;

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 設問の順番を入れ替えます。
   */
  const onMove = (questionIndex: number, upper: boolean) => {
    // 先頭の場合は上に移動不可
    if (upper && questionIndex === 0) {
      return;
    }
    // 最終行の場合は下に移動不可
    if (!upper && questionIndex === fields.length - 1) {
      return;
    }
    // 要素の入れ替え
    move(questionIndex, upper ? questionIndex - 1 : questionIndex + 1);
  };

  /**
   * 設問を削除します。
   */
  const onRemove = (questionIndex: number) => {
    // 1件以下の場合は削除不可
    if (fields.length <= 1) {
      return;
    }
    // 要素の削除
    remove(questionIndex);
  };

  /**
   * 設問を追加します。
   */
  const onAppend = () => {
    // 上限値の以上の場合は追加不可
    if (fields.length >= limit) {
      return;
    }
    // 要素の追加
    append({
      options: [{}, {}, {}, {}],
    });
  };

  /**
   * AIによる設問作成を行います。
   */
  const onCreateOptions = (questionIndex: number) => {
    submit(processing, setProcessing, async () => {
      try {
        const values = getValues();
        const questions = getValues().questions;
        let isValid = true;

        clearErrors();

        if (isEmpty(questions[questionIndex].title)) {
          setError(`questions.${questionIndex}.title` as Path<DiagnosisEditFormStateType>, {
            type: 'required',
          });
          isValid = false;
        }

        if (isEmpty(values.title)) {
          setError('title' as Path<DiagnosisEditFormStateType>, {
            type: 'required',
          });
          isValid = false;
        }

        if (isEmpty(values.description)) {
          setError('description' as Path<DiagnosisEditFormStateType>, {
            type: 'required',
          });
          isValid = false;
        }

        values.results.forEach((result, resultIndex) => {
          isValid = _validRequired(values, `results.${resultIndex}.title`) && isValid;
        });

        if (!isValid) {
          return;
        }
        const resultTitles = values.results.map(result => result.title);

        const response = await postDiagnosisAiOptions({
          companyId: acctState.company.id,
          title: values.title || '',
          description: values.description || '',
          questionTitle: questions[questionIndex].title,
          resultTitles: resultTitles,
        });

        const results = values.results.map(result => {
          return {
            id: result.id,
            title: result.title,
            descriptionHtml: result.descriptionHtml,
            imageKey: result.imageKey,
            isNew: result.isNew,
          };
        });

        const options = response.options.map(option => {
          return {
            title: option.title,
            diagnosisResultList: convResultListTitleToId(option.diagnosisResultList, results),
          };
        });

        // 先に配列を空にする
        questions[questionIndex].options = [...options];

        // 要素の更新
        remove(questionIndex);
        setValue('questions', questions);

        // AIテキスト生成数の更新
        setAcctState(prev => ({
          ...prev,
          company: {
            ...prev.company,
            aiTextUsageCount: response?.aiTextUsageCount ?? acctState.company.aiTextUsageCount,
          },
        }));

        noticeMessageSuccess('AIによる選択肢生成が完了しました。');
      } catch (e) {
        showBoundary(e);
      }
    });
  };

  /**
   * フォームの必須検証をします。
   */
  const _validRequired = (values: DiagnosisEditFormStateType, name: string): boolean => {
    const value = get(values, name);

    if (isEmpty(value)) {
      setError(name as Path<DiagnosisEditFormStateType>, {
        type: 'custom',
        message: '必須項目です。',
      });
      return false;
    }
    return true;
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * AIテキスト生成数の上限に達しているかどうかを判定します。
   */
  const _isAiTextCountLimit = (): boolean => {
    return acctState.company.aiTextUsageCount >= (activePlan?.aiTextCountLimit ?? 0);
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* ローディング */}
      <IndicatorScreen visible={processing} />

      <section className="section-form-default section-question">
        <div className="container">
          {getValues()?.questions.map((question, questionIndex) => {
            return (
              <div key={`ws-quiz-edit-questions-${questionIndex}`} className="box-common mb-3">
                <h3 className="title-section">設問{questionIndex + 1}</h3>

                <div className="form-default-container">
                  <div className="row row-0">
                    <div className="col-md col-header">
                      <div className="card form-header">
                        <h4 className="title">設問用の画像</h4>
                        <FormCompanyImage
                          control={control}
                          name={`questions.${questionIndex}.questionImageKey`}
                          readOnly={props.readOnly}
                        />
                      </div>
                    </div>
                    <div className="col-md col-form ps-md-5 ms-md-2">
                      <div className="btn-group-header text-end">
                        <div className="btn-group">
                          <button
                            className="btn"
                            onClick={() => onMove(questionIndex, true)}
                            disabled={props.readOnly || questionIndex === 0}
                          >
                            <img src="/img/icon/arrow-up-black.svg" alt="" />
                          </button>
                          <button
                            className="btn"
                            onClick={() => onMove(questionIndex, false)}
                            disabled={props.readOnly || questionIndex === fields.length - 1}
                          >
                            <img src="/img/icon/arrow-up-black.svg" alt="" className="scaley-1" />
                          </button>
                          <button
                            className="btn"
                            onClick={() => onRemove(questionIndex)}
                            disabled={props.readOnly || fields.length <= 1}
                          >
                            <img src="/img/icon/trash.svg" className="filter-black" />
                          </button>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="privacy-policy" className="form-label">
                          設問
                        </label>
                        <div className="row align-items-center">
                          <div className="col-md">
                            <FormText
                              name={`questions.${questionIndex}.title`}
                              placeholder="設問を入れよう"
                              register={register}
                              errors={errors}
                              readOnly={props.readOnly}
                              maxLength={100}
                            />
                          </div>
                          <div className="col-md col-btn flex-grow-0">
                            <div className="btn-container max-width-130 mt-md-0">
                              <button
                                onClick={() => onCreateOptions(questionIndex)}
                                disabled={props.readOnly || _isAiTextCountLimit()}
                                className="btn btn-common ai-generate w-100"
                              >
                                選択肢の生成
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* オプション */}
                      <WsDiagnosisEditQuestionOptions
                        questionIndex={questionIndex}
                        readOnly={props.readOnly}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="btn-container max-width-238 mx-auto">
            <button
              onClick={onAppend}
              disabled={fields.length >= limit || props.readOnly}
              className="btn btn-common moderate-blue w-100"
            >
              + 設問を追加
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
